import { forwardRef } from 'react';
import { ChevronDown, AlertCircle, CheckCircle } from 'lucide-react';

/**
 * Select Component - A styled dropdown select input
 * 
 * @param {Object} props - Component props
 * @param {string} props.label - Select label
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.hint - Helper text below select
 * @param {string} props.error - Error message
 * @param {boolean} props.success - Shows success state
 * @param {string} props.size - Select size: 'sm', 'md', 'lg'
 * @param {boolean} props.disabled - Disables the select
 * @param {Array} props.options - Array of options: [{ value: '', label: '' }]
 * @param {string} props.className - Additional CSS classes
 */
const Select = forwardRef(({
  label,
  placeholder = 'Seleccionar...',
  hint,
  error,
  success,
  size = 'md',
  disabled = false,
  options = [],
  className = '',
  id,
  ...props
}, ref) => {
  const inputId = id || `select-${Math.random().toString(36).substr(2, 9)}`;

  // Size classes
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-xs pr-8',
    md: 'px-4 py-2 text-sm pr-10',
    lg: 'px-5 py-3 text-base pr-12',
  };

  // State classes
  const getStateClasses = () => {
    if (error) return 'border-error focus:ring-error focus:border-error';
    if (success) return 'border-success focus:ring-success focus:border-success';
    return 'border-gray-300 focus:ring-primary-500 focus:border-transparent';
  };

  return (
    <div className={`form-group ${className}`}>
      {label && (
        <label htmlFor={inputId} className="form-label">
          {label}
        </label>
      )}
      <div className="relative">
        <select
          ref={ref}
          id={inputId}
          disabled={disabled}
          className={`
            w-full rounded-lg border appearance-none transition-all duration-200 ease-in-out
            focus:outline-none focus:ring-2 focus:ring-offset-0
            disabled:bg-gray-100 disabled:cursor-not-allowed
            bg-white cursor-pointer
            ${sizeClasses[size]}
            ${getStateClasses()}
          `.trim().replace(/\s+/g, ' ')}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={hint ? `${inputId}-hint` : error ? `${inputId}-error` : undefined}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        
        {/* Chevron icon */}
        <div className="absolute inset-y-0 right-0 flex items-center pointer-events-none text-gray-400">
          <ChevronDown className="w-5 h-5 mr-3" />
        </div>
        
        {/* Status icons */}
        {error && (
          <div className="absolute inset-y-0 right-8 flex items-center pointer-events-none">
            <AlertCircle className="w-5 h-5 text-error" />
          </div>
        )}
        {success && !error && (
          <div className="absolute inset-y-0 right-8 flex items-center pointer-events-none">
            <CheckCircle className="w-5 h-5 text-success" />
          </div>
        )}
      </div>
      
      {hint && !error && (
        <p id={`${inputId}-hint`} className="form-hint">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${inputId}-error`} className="form-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
});

Select.displayName = 'Select';

/**
 * MultiSelect Component - Select multiple options (simplified version)
 */
export const MultiSelect = forwardRef(({
  label,
  placeholder = 'Seleccionar...',
  hint,
  error,
  options = [],
  value = [],
  onChange,
  className = '',
  id,
  ...props
}, ref) => {
  const inputId = id || `multiselect-${Math.random().toString(36).substr(2, 9)}`;

  const handleToggle = (optionValue) => {
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    onChange?.({ target: { value: newValue, name: props.name } });
  };

  return (
    <div className={`form-group ${className}`}>
      {label && (
        <label className="form-label">
          {label}
        </label>
      )}
      <div className="border border-gray-300 rounded-lg p-2 focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent">
        <div className="flex flex-wrap gap-2">
          {value.length === 0 && (
            <span className="text-gray-400 text-sm py-1">{placeholder}</span>
          )}
          {options.filter(opt => value.includes(opt.value)).map((option) => (
            <span
              key={option.value}
              className="inline-flex items-center px-2 py-1 bg-primary-100 text-primary-700 rounded text-sm"
            >
              {option.label}
              <button
                type="button"
                onClick={() => handleToggle(option.value)}
                className="ml-1 hover:text-primary-900"
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <div className="mt-2 border-t pt-2 max-h-40 overflow-y-auto">
          {options.map((option) => (
            <label
              key={option.value}
              className="flex items-center px-2 py-1 hover:bg-gray-50 cursor-pointer rounded"
            >
              <input
                type="checkbox"
                checked={value.includes(option.value)}
                onChange={() => handleToggle(option.value)}
                className="mr-2 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm">{option.label}</span>
            </label>
          ))}
        </div>
      </div>
      {hint && !error && <p className="form-hint">{hint}</p>}
      {error && <p className="form-error" role="alert">{error}</p>}
    </div>
  );
});

MultiSelect.displayName = 'MultiSelect';

export default Select;